import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#handleSizeGuideNavigation();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Handle smooth scroll for accordion opening, especially for size guide
    if (!this.details.open) {
      // Use setTimeout to wait for the accordion to open
      setTimeout(() => {
        this.#scrollAccordionIntoView();
      }, 100);
    }
  };

  /**
   * Scrolls the accordion into view when opened, focusing on the right side container
   */
  #scrollAccordionIntoView() {
    // Find the right side container (product information section)
    const rightContainer = this.closest('.product-information') || 
                          this.closest('[class*="product-details"]') ||
                          this.closest('.main-content');
    
    if (rightContainer && this.details.open) {
      // Check if this is likely the garment measurements/size guide section
      const hasGarmentContent = this.textContent.toLowerCase().includes('garment') ||
                               this.textContent.toLowerCase().includes('measurement') ||
                               this.querySelector('#sizeGuideContent') ||
                               this.id.includes('garment') ||
                               this.className.includes('garment');
      
      if (hasGarmentContent) {
        // Get the bounds of the opened accordion content
        const accordionRect = this.getBoundingClientRect();
        const containerRect = rightContainer.getBoundingClientRect();
        
        // Calculate if we need to scroll
        const accordionBottom = accordionRect.bottom;
        const containerBottom = containerRect.bottom;
        const viewportHeight = window.innerHeight;
        
        // Only scroll if the content is not fully visible
        if (accordionBottom > viewportHeight || accordionRect.top < 100) {
          // Scroll the right container so the accordion content is visible
          const scrollOffset = accordionRect.top - containerRect.top - 20; // 20px padding from top
          
          rightContainer.scrollTo({
            top: rightContainer.scrollTop + scrollOffset,
            behavior: 'smooth'
          });
        }
      }
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * Handles size guide navigation for garment measurements accordion
   */
  #handleSizeGuideNavigation() {
    // Check if this is the garment measurements accordion
    const isGarmentAccordion = this.textContent.toLowerCase().includes('garment') && 
                              this.textContent.toLowerCase().includes('measurement');
    
    if (isGarmentAccordion) {
      // Handle direct navigation from URL hash
      if (window.location.hash === '#sizeGuideContent') {
        setTimeout(() => {
          this.details.open = true;
          setTimeout(() => {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }, 500);
      }
      
      // Handle size guide link clicks
      document.querySelectorAll('a[href="#sizeGuideContent"]').forEach(link => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.details.open = true;
          setTimeout(() => {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        });
      });
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
